import { LEVEL_IDS, type LevelId } from '../data/ids';
import { betterJump } from './jump/jump';
import type { JumpGrade, RunState } from './run';

/** Linear unlocking (GDD §10.1): "1-1" > "1-2", "1-3" > "2-1", "4-3" > null. */
export function nextLevel(id: LevelId): LevelId | null {
  return LEVEL_IDS[LEVEL_IDS.indexOf(id) + 1] ?? null;
}

const inCampaignOrder = (ids: Iterable<LevelId>): LevelId[] =>
  [...new Set(ids)].sort((a, b) => LEVEL_IDS.indexOf(a) - LEVEL_IDS.indexOf(b));

export interface LevelResult {
  /** Points earned in the level: the total score minus the score when it started (GDD §12). */
  levelScore: number;
  /** Total score of the game when the level ends; the next level starts from it. */
  totalScore: number;
  /** null in airstrip levels, which have no El Salto (GDD §6.4). */
  jumpGrade?: JumpGrade | null;
  /** Computed by #43; false until then. */
  cleanRun?: boolean;
}

/**
 * Completing a level (GDD §6.2 rule 7, §10.1, §12): its checkpoint is cleared, the next level is
 * unlocked once, the best score keeps the maximum and the current level moves on to the next one.
 * After the last level (4-3) it stays as the current level. Replaying a level applies the same rule.
 */
export function completeLevel(state: RunState, result: LevelResult): void {
  const id = state.levelId;
  const next = nextLevel(id);
  const previous = state.completedLevels[id];
  // Marks of the assist mode (GDD §13.6, #138): an unassisted record is never replaced by an assisted run; an assisted one is
  // replaced by any higher score, and loses its mark when the new run is unassisted.
  if (!previous || state.assisted === false || previous.assisted) {
    const higher = !previous || result.levelScore > previous.bestScore;
    state.completedLevels[id] = {
      bestScore: Math.max(previous?.bestScore ?? 0, result.levelScore),
      // The best jump only changes when the new one is better (GDD §6.4, #93).
      bestJump: betterJump(previous?.bestJump ?? null, result.jumpGrade ?? null),
      cleanRun: (result.cleanRun ?? false) || (previous?.cleanRun ?? false),
      assisted: higher ? state.assisted : (previous?.assisted ?? state.assisted),
    };
  }
  state.unlockedLevels = inCampaignOrder(next ? [...state.unlockedLevels, next] : state.unlockedLevels);
  state.checkpointId = null;
  state.levelId = next ?? id;
  state.scoreAtLevelStart = result.totalScore;
  state.cleanRun = true;
}

/** Starts (or replays) an unlocked level from its beginning. */
export function startLevel(state: RunState, id: LevelId, totalScore: number): void {
  if (!state.unlockedLevels.includes(id)) throw new Error(`level ${id} is locked`);
  state.levelId = id;
  state.checkpointId = null;
  state.scoreAtLevelStart = totalScore;
  state.outcome = 'EN_CURSO';
  state.cleanRun = true;
}
